/**
 * Apple Health Kit integration via @capgo/capacitor-health
 * 
 * Data sync frequency (iOS):
 * - HealthKit background delivery: ~4-15 seconds of runtime per hour when new data arrives
 * - NOT every minute: iOS limits background updates to preserve battery
 * - Data is delivered when the Health app receives new entries (from iPhone, Apple Watch, etc.)
 * - For real-time data: read when app is in foreground
 */

import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import type { HealthSample, HealthDataType } from '@capgo/capacitor-health';

let Health: typeof import('@capgo/capacitor-health').Health | null = null;

const getHealth = async () => {
  if (!Health && Capacitor.isNativePlatform()) {
    Health = (await import('@capgo/capacitor-health')).Health;
  }
  return Health;
};

const HEALTH_CONNECTED_KEY = 'straiv_health_connected';
const FIRST_SYNC_FEEDBACK_KEY = 'straiv_health_first_sync_toast_shown';
const LAST_SYNC_KEY = (uid: string) => `straiv_health_last_sync_${uid}`;
const LAST_TOAST_KEY = (uid: string) => `straiv_health_last_toast_${uid}`;
const TOAST_MIN_INTERVAL_MS = 5 * 60 * 1000;

function showFirstSyncSuccessFeedback() {
  if (localStorage.getItem(FIRST_SYNC_FEEDBACK_KEY)) return;
  localStorage.setItem(FIRST_SYNC_FEEDBACK_KEY, '1');

  // Native alert (iOS) - always visible, works when WebView may not be ready
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    const plugin = getStraivHealthConnect();
    if (plugin?.showSyncSuccessAlert) plugin.showSyncSuccessAlert().catch(() => {});
  }

  // Web toast as backup (may not render if WebView not ready right after native dismiss)
  requestAnimationFrame(() => {
    import('sonner').then(({ toast }) => {
      toast.success('Apple Health data successfully synced');
    });
  });
}

type StraivHealthConnectPlugin = {
  presentNativeConnect: (opts?: { userId?: string }) => Promise<{ readAuthorized: string[]; readDenied: string[] }>;
  presentHealthScreen: () => Promise<void>;
  readLatestValues: () => Promise<Record<string, unknown>>;
  readLast30Days: () => Promise<{ days: Record<string, unknown>[] }>;
  readAllSamples: () => Promise<{ samples: HealthSampleRow[] }>;
  readHeartRateSamples: (opts: {
    startDate: string;
    endDate: string;
    limit?: number;
  }) => Promise<{ samples: Array<{ value: number; start_time: string; end_time?: string }> }>;
  readLatestHeartRate: () => Promise<{ sample: { value: number; start_time: string; end_time?: string } | null }>;
  isHealthConnected: (opts?: { userId?: string }) => Promise<{ connected: boolean }>;
  setHealthConnected: (opts?: { userId?: string }) => Promise<void>;
  setHealthSyncCredentials?: (opts: { accessToken: string; userId: string; supabaseUrl?: string; supabaseAnonKey?: string }) => Promise<void>;
  clearHealthConnected: (opts: { userId: string }) => Promise<void>;
  showSyncSuccessAlert: () => Promise<void>;
  addListener?(eventName: 'healthDataLoaded', listenerFunc: () => void): Promise<PluginListenerHandle>;
};
let _straivHealthConnect: StraivHealthConnectPlugin | null = null;

export type HealthSampleRow = {
  sample_type: string;
  value: number;
  unit: string;
  start_time: string;
  end_time: string;
  source_id: string;
};

function getStraivHealthConnect() {
  if (!_straivHealthConnect && Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    _straivHealthConnect = registerPlugin<StraivHealthConnectPlugin>('StraivHealthConnect');
  }
  return _straivHealthConnect;
}

/** Register listener for when native Health screen loads data. Runs sync so alert can show while user is still on native screen. */
export function addHealthDataLoadedListener(onDataLoaded: () => void | Promise<void>): (() => void) | null {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return null;
  const plugin = getStraivHealthConnect();
  if (!plugin?.addListener) return null;
  const ref = { current: null as PluginListenerHandle | null };
  plugin.addListener('healthDataLoaded', () => {
    void Promise.resolve(onDataLoaded());
  }).then((h) => { ref.current = h; });
  return () => {
    ref.current?.remove();
  };
}

/** Present native Connect-only flow (iOS). Shows Connect UI, dismisses immediately after user connects, no data view. Returns true if user connected. */
export async function presentHealthScreenNative(options?: { syncOnDismiss?: boolean; userId?: string; skipFeedback?: boolean }): Promise<boolean> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return false;
  const plugin = getStraivHealthConnect();
  if (!plugin) return false;
  const { supabase } = await import('@/integrations/supabase/client');
  const { data: { session } } = await supabase.auth.getSession();
  const userId = options?.userId ?? session?.user?.id ?? undefined;
  const result = await plugin.presentNativeConnect({ userId });
  const authorized = result?.readAuthorized ?? [];
  if (authorized.length > 0) {
    await persistHealthConnectedState(userId);
    if (options?.syncOnDismiss && session?.user) {
      // Ensure native has credentials for background sync before running 30-day sync
      try {
        const { SUPABASE_URL, SUPABASE_ANON_KEY } = await import('@/lib/env');
        const plugin = getStraivHealthConnect();
        if (plugin?.setHealthSyncCredentials) {
          await plugin.setHealthSyncCredentials({
            accessToken: session.access_token,
            userId: session.user.id,
            supabaseUrl: SUPABASE_URL,
            supabaseAnonKey: SUPABASE_ANON_KEY,
          });
        }
      } catch {
        // Ignore - credentials may already be set
      }
      await syncHealthToSupabase(session.user.id, { forceSync: true, skipFeedback: options.skipFeedback });
    }
    return true;
  }
  return false;
}

/** Persist connected state (localStorage fallback; iOS uses native UserDefaults). Per-user when userId provided. */
export function setHealthConnectedState(connected: boolean, userId?: string) {
  try {
    const key = userId ? `${HEALTH_CONNECTED_KEY}_${userId}` : HEALTH_CONNECTED_KEY;
    if (connected) {
      localStorage.setItem(key, Date.now().toString());
    } else {
      localStorage.removeItem(key);
    }
  } catch {}
}

/** Persist connected state in native storage (iOS). Call when we know we have permission. Per-user when userId provided. */
export async function persistHealthConnectedState(userId?: string): Promise<void> {
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    const plugin = getStraivHealthConnect();
    if (plugin) {
      try {
        await plugin.setHealthConnected({ userId });
      } catch {
        // ignore
      }
    }
  }
  setHealthConnectedState(true, userId);
}

/** Disconnect Apple Health for this user. Clears connection state (native + localStorage). Data in Supabase is preserved. */
export async function disconnectAppleHealth(userId: string): Promise<void> {
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    const plugin = getStraivHealthConnect();
    if (plugin) {
      try {
        await plugin.clearHealthConnected({ userId });
      } catch {
        // ignore
      }
    }
  }
  setHealthConnectedState(false, userId);
}

/**
 * Synchronous read of the last-known Apple Health connect flag from localStorage
 * (same TTL rule as getHealthConnectedState). Use for hook initial state to
 * avoid a one-frame "disconnected" flash before the async native check returns.
 */
export function getHealthConnectedCacheSync(userId?: string): boolean {
  try {
    const key = userId ? `${HEALTH_CONNECTED_KEY}_${userId}` : HEALTH_CONNECTED_KEY;
    const cached = localStorage.getItem(key);
    if (!cached) return false;
    const ts = parseInt(cached, 10);
    if (isNaN(ts)) return false;
    return Date.now() - ts < 7 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

/** Check if we have a recent "connected" record. On iOS, prefers native UserDefaults. Pass userId for per-user state (required for multi-account on same device). */
export async function getHealthConnectedState(userId?: string): Promise<boolean> {
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    const plugin = getStraivHealthConnect();
    if (plugin) {
      try {
        const { connected } = await plugin.isHealthConnected({ userId });
        return connected;
      } catch {
        // fall through to localStorage
      }
    }
  }
  try {
    const key = userId ? `${HEALTH_CONNECTED_KEY}_${userId}` : HEALTH_CONNECTED_KEY;
    const cached = localStorage.getItem(key);
    if (!cached) return false;
    const ts = parseInt(cached, 10);
    if (isNaN(ts)) return false;
    return Date.now() - ts < 7 * 24 * 60 * 60 * 1000; // 7 days
  } catch {
    return false;
  }
}

/** Raw values from native (distance in meters). Use for Supabase sync. */
export async function readLatestRawFromNative(): Promise<{
  steps: number | null;
  distance_m: number | null;
  calories: number | null;
  basalCalories: number | null;
  flightsClimbed: number | null;
  exerciseMinutes: number | null;
  standHours: number | null;
  heartRate: number | null;
  restingHeartRate: number | null;
  heartRateMin: number | null;
  heartRateMax: number | null;
  heartRateAvg: number | null;
  weight: number | null;
  bodyFatPct: number | null;
  height_cm: number | null;
  vo2Max: number | null;
  sleepHours: number | null;
  healthData: Record<string, number>;
}> {
  const plugin = getStraivHealthConnect();
  if (!plugin) {
    return {
      steps: null, distance_m: null, calories: null, basalCalories: null,
      flightsClimbed: null, exerciseMinutes: null, standHours: null,
      heartRate: null, restingHeartRate: null, heartRateMin: null, heartRateMax: null, heartRateAvg: null,
      weight: null, bodyFatPct: null, height_cm: null, vo2Max: null, sleepHours: null,
      healthData: {},
    };
  }

  const result = await plugin.readLatestValues() as Record<string, unknown>;
  const healthData = (result.healthData as Record<string, number>) ?? {};
  return {
    steps: (result.steps as number) ?? null,
    distance_m: (result.distance as number) ?? null,
    calories: (result.calories as number) ?? null,
    basalCalories: (result.basalCalories as number) ?? null,
    flightsClimbed: (result.flightsClimbed as number) ?? null,
    exerciseMinutes: (result.exerciseMinutes as number) ?? null,
    standHours: (result.standHours as number) ?? null,
    heartRate: (result.heartRate as number) ?? null,
    restingHeartRate: (result.restingHeartRate as number) ?? null,
    heartRateMin: (result.heartRateMin as number) ?? null,
    heartRateMax: (result.heartRateMax as number) ?? null,
    heartRateAvg: (result.heartRateAvg as number) ?? null,
    weight: (result.weight as number) ?? null,
    bodyFatPct: (result.bodyFatPct as number) ?? null,
    height_cm: result.height != null ? (result.height as number) * 100 : null,
    vo2Max: (result.vo2Max as number) ?? null,
    sleepHours: (result.sleepHours as number) ?? null,
    healthData,
  };
}

/** Read latest values from native HealthKit (direct, bypasses capacitor-health). */
export async function readLatestFromNative(): Promise<Record<HealthDataType, number | null>> {
  const raw = await readLatestRawFromNative();
  return {
    steps: raw.steps,
    distance: raw.distance_m != null ? raw.distance_m / 1000 : null, // meters -> km
    calories: raw.calories,
    heartRate: raw.heartRate,
    weight: raw.weight,
  };
}

/** Native full-screen Health connect (iOS only). Bypasses Capacitor bridge - direct HKHealthStore. */
export const requestHealthAuthorizationViaNative = async (): Promise<{
  readAuthorized: HealthDataType[];
  readDenied: HealthDataType[];
}> => {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
    throw new Error('Native Health connect is only available on iOS');
  }
  const plugin = getStraivHealthConnect();
  if (!plugin) throw new Error('StraivHealthConnect not available');
  const result = await plugin.presentNativeConnect();
  return {
    readAuthorized: (result.readAuthorized || []) as HealthDataType[],
    readDenied: (result.readDenied || []) as HealthDataType[],
  };
};

export type HealthAvailabilityResult = { available: boolean; reason?: string };

export const isHealthAvailable = async (): Promise<HealthAvailabilityResult> => {
  const HealthModule = await getHealth();
  if (!HealthModule) return { available: false, reason: 'Health plugin not loaded' };
  const { available, reason } = await HealthModule.isAvailable();
  return { available, reason };
};

export const requestHealthAuthorization = async (
  skipAvailabilityCheck = false,
  useNativePrompt = true,
  /** Premium: request broader HealthKit write types (native connect still uses full HK sheet). */
  options?: { extendedWrite?: boolean },
) => {
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    try {
      console.log('[AppleHealth] Using native StraivHealthConnect flow');
      return await requestHealthAuthorizationViaNative();
    } catch (e) {
      console.warn('[AppleHealth] Native connect failed, falling back to bridge:', e);
    }
  }

  const HealthModule = await getHealth();
  if (!HealthModule) throw new Error('Health Kit is not available on this device');

  if (!skipAvailabilityCheck) {
    const result = await HealthModule.isAvailable();
    if (!result.available) throw new Error(result.reason || 'Health Kit unavailable');
  }

  const writeTypes = options?.extendedWrite
    ? (['weight', 'bodyMass', 'stepCount', 'activeEnergyBurned'] as string[])
    : (['weight'] as string[]);

  const opts = {
    read: ['steps', 'distance', 'calories', 'heartRate', 'weight'] as string[],
    write: writeTypes,
  };

  const H = HealthModule as unknown as {
    requestAuthorization: (o: typeof opts) => Promise<{ readAuthorized: string[]; readDenied: string[] }>;
    requestAuthorizationWithNativePrompt?: (o: typeof opts) => Promise<{ readAuthorized: string[]; readDenied: string[] }>;
  };
  if (useNativePrompt && typeof H.requestAuthorizationWithNativePrompt === 'function') {
    return H.requestAuthorizationWithNativePrompt(opts);
  }
  return (HealthModule as unknown as typeof H).requestAuthorization(opts);
};

export const checkHealthAuthorization = async (skipAvailabilityCheck = false) => {
  const HealthModule = await getHealth();
  if (!HealthModule) return null;

  if (!skipAvailabilityCheck) {
    const { available } = await HealthModule.isAvailable();
    if (!available) return null;
  }

  return HealthModule.checkAuthorization({
    read: ['steps', 'distance', 'calories', 'heartRate', 'weight'],
    write: ['weight'],
  });
};

const READ_SAMPLE_TIMEOUT_MS = 15_000;

export type LiveHeartRateSample = { timestamp: number; bpm: number };

/** Latest heart-rate beat in HealthKit (Watch syncs in the background — no workout app required). */
export async function readLatestHeartRateBpm(maxAgeMs = 120_000): Promise<LiveHeartRateSample | null> {
  const now = Date.now();
  const accept = (sample: LiveHeartRateSample | null) => {
    if (!sample) return null;
    if (now - sample.timestamp > maxAgeMs) return null;
    return sample;
  };

  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    const plugin = getStraivHealthConnect();
    if (plugin?.readLatestHeartRate) {
      try {
        const { sample } = await plugin.readLatestHeartRate();
        if (sample?.start_time && Number.isFinite(sample.value)) {
          const bpm = Number(sample.value);
          if (bpm >= 30 && bpm <= 240) {
            return accept({ timestamp: new Date(sample.start_time).getTime(), bpm });
          }
        }
      } catch (e) {
        console.warn('[AppleHealth] readLatestHeartRate failed:', e);
      }
    }
  }

  const end = new Date();
  const start = new Date(end.getTime() - Math.min(maxAgeMs, 6 * 60 * 60 * 1000));
  const recent = await readHeartRateSamplesInRange(start, end, 5);
  const latest = recent.length > 0 ? recent[recent.length - 1] : null;
  return accept(latest);
}

/** Heart-rate samples in a window — native Straiv HK query on iOS, capgo fallback elsewhere. */
export async function readHeartRateSamplesInRange(
  startDate: Date,
  endDate: Date,
  limit = 200,
): Promise<LiveHeartRateSample[]> {
  const withTimeout = <T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> =>
    Promise.race([
      promise,
      new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms)),
    ]);

  const normalize = (rows: Array<{ value: number; start_time?: string; startDate?: string }>) =>
    rows
      .map((s) => {
        const tsRaw = s.start_time ?? s.startDate;
        return { timestamp: tsRaw ? new Date(tsRaw).getTime() : NaN, bpm: Number(s.value) };
      })
      .filter((s) => Number.isFinite(s.timestamp) && Number.isFinite(s.bpm) && s.bpm >= 30 && s.bpm <= 240)
      .sort((a, b) => a.timestamp - b.timestamp);

  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    const plugin = getStraivHealthConnect();
    if (plugin?.readHeartRateSamples) {
      try {
        const { samples } = await withTimeout(
          plugin.readHeartRateSamples({
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            limit,
          }),
          2500,
          { samples: [] as Array<{ value: number; start_time?: string }> },
        );
        const native = normalize(samples ?? []);
        if (native.length > 0) return native;
      } catch (e) {
        console.warn('[AppleHealth] readHeartRateSamples native failed:', e);
      }
    }
  }

  const capgoSamples = await withTimeout(
    readHealthSamples('heartRate', { startDate, endDate, limit }),
    2500,
    [] as HealthSample[],
  );
  return normalize(
    capgoSamples.map((s) => {
      const raw = s as HealthSample & { start_time?: string };
      return { value: Number(raw.value), start_time: raw.startDate ?? raw.start_time };
    }),
  );
}

export const readHealthSamples = async (
  dataType: HealthDataType,
  options?: { startDate?: Date; endDate?: Date; limit?: number }
): Promise<HealthSample[]> => {
  const HealthModule = await getHealth();
  if (!HealthModule) return [];

  const endDate = options?.endDate || new Date();
  const startDate = options?.startDate || new Date(endDate.getTime() - 24 * 60 * 60 * 1000);

  const readPromise = HealthModule.readSamples({
    dataType,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    limit: options?.limit ?? 100,
    ascending: false,
  });

  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error(`Read ${dataType} timed out after ${READ_SAMPLE_TIMEOUT_MS / 1000}s`)), READ_SAMPLE_TIMEOUT_MS)
  );

  try {
    const { samples } = await Promise.race([readPromise, timeoutPromise]);
    return samples ?? [];
  } catch (e) {
    console.warn(`[AppleHealth] readSamples(${dataType}) failed:`, e);
    return [];
  }
};

export const HEALTH_DATA_TYPES = ['steps', 'distance', 'calories', 'heartRate', 'weight'] as const;

/** Sync individual HealthKit samples to Supabase. Each data point has timestamp (e.g. 2500 steps at 13:00). */
export async function syncSamplesToSupabase(userId: string): Promise<{ synced: boolean; count?: number; error?: string }> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
    return { synced: false, error: 'Only iOS native' };
  }
  const connected = await getHealthConnectedState(userId);
  if (!connected) return { synced: false, error: 'Health not connected' };

  try {
    const plugin = getStraivHealthConnect();
    if (!plugin) return { synced: false, error: 'Plugin not available' };

    console.log('[AppleHealth] Reading samples (may take 1–2 min)...');
    const result = await plugin.readAllSamples();
    const rawSamples = result.samples ?? [];
    console.log('[AppleHealth] readAllSamples returned', rawSamples.length, 'raw samples');

    // Accept samples with source_id, or generate stable fallback for dedup
    const samples = (rawSamples as unknown[])
      .filter((s): s is Record<string, unknown> => s != null && typeof s === 'object' && typeof (s as Record<string, unknown>).sample_type === 'string')
      .map((s: Record<string, unknown>, i: number) => {
        const sid = s.source_id;
        const id = typeof sid === 'string' && sid ? sid : `gen_${i}_${s.start_time}_${s.sample_type}_${s.value}`;
        return { ...(s as Record<string, unknown>), source_id: id } as Record<string, unknown>;
      });

    if (samples.length === 0) {
      console.warn('[AppleHealth] No samples to sync');
      return { synced: true, count: 0 };
    }

    const { supabase } = await import('@/integrations/supabase/client');

    // Batch upsert (500 per batch to avoid payload limits)
    const BATCH = 500;
    for (let i = 0; i < samples.length; i += BATCH) {
      const batch = samples.slice(i, i + BATCH).map((s: Record<string, unknown>) => ({
        user_id: userId,
        sample_type: s.sample_type as string,
        value: s.value as number,
        unit: (s.unit as string) ?? null,
        start_time: s.start_time as string,
        end_time: (s.end_time as string) ?? null,
        source_id: s.source_id as string,
        source: 'apple_health',
      }));
      const { error } = await supabase.from('health_samples').upsert(batch, {
        onConflict: 'user_id,source_id',
        ignoreDuplicates: false,
      });
      if (error) {
        console.error('[AppleHealth] health_samples upsert failed:', error.message);
        return { synced: false, error: error.message };
      }
    }
    console.log('[AppleHealth] Synced', samples.length, 'samples to health_samples');
    return { synced: true, count: samples.length };
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Sample sync failed';
    console.error('[AppleHealth] syncSamplesToSupabase error:', msg);
    return { synced: false, error: msg };
  }
}

/** Clamp/round numeric values to fit health_metrics column constraints. Prevents "numeric field overflow". */
function sanitizeNumeric(
  val: number | null | undefined,
  max: number,
  decimals = 2
): number | null {
  if (val == null || !Number.isFinite(val)) return null;
  const rounded = Math.round(val * Math.pow(10, decimals)) / Math.pow(10, decimals);
  const clamped = Math.max(-max, Math.min(max, rounded));
  return clamped;
}

/** Sanitize health_data JSONB: remove non-finite values, clamp large numbers. */
function sanitizeHealthData(raw: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  const MAX_SAFE = 999_999_999; // Avoid JSON/DB overflow
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    if (v < 0) continue; // health_data typically stores positive metrics
    const clamped = Math.min(MAX_SAFE, Math.round(v * 1000) / 1000);
    if (clamped > 0 && clamped < 1e15) out[k] = clamped; // exclude Infinity-like
  }
  return out;
}

function clampScore0to100(n: number): number {
  return Math.min(100, Math.max(0, n));
}

/**
 * Stress score 0–100 from HRV, RHR, respiratory rate. Higher = less physiological stress / better recovery signal.
 * Uses continuous curves (not flat buckets) so the score moves day-to-day as inputs change.
 */
function computeStressScore(row: Record<string, unknown>): number | null {
  const hrv = row.hrv_sdnn_ms as number | undefined;
  const rhr = row.resting_heart_rate_bpm as number | undefined;
  const resp = row.respiratory_rate_per_min as number | undefined;
  const hd = row.health_data as Record<string, number> | undefined;
  const respRate = resp ?? hd?.respiratory_rate_per_min ?? hd?.HKQuantityTypeIdentifierRespiratoryRate;

  let hrvScore: number | null = null;
  if (hrv != null && hrv > 0) {
    // Typical SDNN ~15–80+ ms; linear map with sensitivity in the mid-range
    hrvScore = clampScore0to100(((hrv - 12) / 55) * 100);
  }

  let rhrScore: number | null = null;
  if (rhr != null && rhr > 0) {
    // Lower resting HR → higher score (within sensible bounds)
    rhrScore = clampScore0to100(100 - ((rhr - 48) / 42) * 100);
  }

  let respScore: number | null = null;
  if (respRate != null && respRate > 0) {
    const r = Number(respRate);
    // Calm resting rate ~12–18/min peaks at 100; gentle falloff outside
    const dist = Math.abs(r - 15);
    respScore = clampScore0to100(100 - (dist / 12) * 100);
  }

  const weights: number[] = [];
  const scores: number[] = [];
  if (hrvScore != null) {
    scores.push(hrvScore);
    weights.push(0.42);
  }
  if (rhrScore != null) {
    scores.push(rhrScore);
    weights.push(0.38);
  }
  if (respScore != null) {
    scores.push(respScore);
    weights.push(0.2);
  }

  if (scores.length === 0) return null;
  const wSum = weights.reduce((a, b) => a + b, 0);
  const blended = scores.reduce((acc, s, i) => acc + s * (weights[i] / wSum), 0);
  return Math.round(clampScore0to100(blended));
}

/** Strain score 0-100 from activity load; heart-rate terms clamped so the score stays 0–100. */
function computeStrainScore(row: Record<string, unknown>): number | null {
  const activeCal = row.active_energy_kcal as number | undefined;
  const exercise = row.exercise_minutes as number | undefined;
  const stress = computeStressScore(row);
  const hrMax = row.heart_rate_max_bpm as number | undefined;
  const hrAvg = row.heart_rate_avg_bpm as number | undefined;
  const parts: number[] = [];
  if (activeCal != null && activeCal > 0) {
    parts.push(clampScore0to100((activeCal / 800) * 100));
  }
  if (exercise != null && exercise > 0) {
    parts.push(clampScore0to100((exercise / 60) * 100));
  }
  if (stress != null) {
    parts.push(clampScore0to100(100 - stress));
  }
  if (hrMax != null && hrMax > 0) {
    parts.push(clampScore0to100(((hrMax - 60) / 90) * 100));
  }
  if (hrAvg != null && hrAvg > 0) {
    parts.push(clampScore0to100(((hrAvg - 60) / 60) * 100));
  }
  if (parts.length === 0) return null;
  const avg = parts.reduce((a, b) => a + b, 0) / parts.length;
  return Math.round(clampScore0to100(avg));
}

/** Persist stress/strain as 0–100 only (no negative artifacts). */
function sanitizeHealthScore0to100(val: number | null | undefined): number | null {
  if (val == null || !Number.isFinite(val)) return null;
  const rounded = Math.round(val * 100) / 100;
  return clampScore0to100(rounded);
}

/** Simple readiness score 0-100 from HRV, sleep, RHR, activity. Stored for Arnold/analytics. */
function computeReadinessScore(row: Record<string, unknown>): number | null {
  const hrv = row.hrv_sdnn_ms as number | undefined;
  const sleep = row.sleep_hours as number | undefined;
  const rhr = row.resting_heart_rate_bpm as number | undefined;
  const exercise = row.exercise_minutes as number | undefined;
  const parts: number[] = [];
  if (hrv != null && hrv > 0) {
    // HRV 20-80 ms typical; normalize to 0-100 (higher=better)
    parts.push(Math.min(100, Math.max(0, (hrv - 15) / 0.65)));
  }
  if (sleep != null && sleep > 0) {
    // 7-9h optimal; 6-10 ok
    const sleepScore = sleep >= 7 && sleep <= 9 ? 100 : sleep >= 6 && sleep <= 10 ? 70 : sleep >= 5 && sleep <= 11 ? 40 : 20;
    parts.push(sleepScore);
  }
  if (rhr != null && rhr > 0) {
    // Lower RHR generally better; 50-70 good
    parts.push(Math.min(100, Math.max(0, 100 - (rhr - 50) * 1.5)));
  }
  if (parts.length === 0) return null;
  return Math.round(parts.reduce((a, b) => a + b, 0) / parts.length);
}

/** Returns true if any day has at least one non-null metric (user actually connected and has data). */
function hasAnyMetricData(days: Record<string, unknown>[]): boolean {
  const metricKeys = ['steps', 'distance_m', 'active_energy_kcal', 'basal_energy_kcal', 'flights_climbed', 'exercise_minutes', 'stand_hours', 'heart_rate_min_bpm', 'heart_rate_max_bpm', 'heart_rate_avg_bpm', 'resting_heart_rate_bpm', 'weight_kg', 'body_fat_pct', 'height_m', 'vo2_max', 'sleep_hours', 'sleep_rem_hours', 'sleep_deep_hours', 'sleep_core_hours', 'sleep_in_bed_hours'];
  return days.some((d) => {
    const hd = d.health_data as Record<string, number> | undefined;
    if (hd && Object.keys(hd).length > 0) return true;
    return metricKeys.some((k) => {
      const v = d[k];
      return v != null && typeof v === 'number' && !Number.isNaN(v) && v > 0;
    });
  });
}

/**
 * Single-flight cache: any concurrent caller for the same userId joins the
 * in-progress sync's promise instead of kicking off a parallel one. Without
 * this, mount + foreground + healthDataLoaded + manual refresh would race
 * and the loser's empty / partial day rows could clobber the winner's good
 * upserts via PostgREST resolution=merge-duplicates.
 */
const _inflightSyncs = new Map<string, Promise<{ synced: boolean; error?: string }>>();

/** Sync HealthKit data to Supabase. Backfills last 30 days - no gaps when user doesn't open app. */
export async function syncHealthToSupabase(
  userId: string,
  options?: { forceSync?: boolean; skipFeedback?: boolean }
): Promise<{ synced: boolean; error?: string }> {
  // De-dupe overlapping calls for the same user. Note: we key on userId only -
  // forceSync / skipFeedback are intentionally ignored so a force-sync joining
  // an in-flight non-force sync still completes; if anything is wrong, the
  // next call (after completion) will run a fresh full sync.
  const existing = _inflightSyncs.get(userId);
  if (existing) return existing;

  const promise = _syncHealthToSupabaseInner(userId, options).finally(() => {
    _inflightSyncs.delete(userId);
  });
  _inflightSyncs.set(userId, promise);
  return promise;
}

async function _syncHealthToSupabaseInner(
  userId: string,
  options?: { forceSync?: boolean; skipFeedback?: boolean }
): Promise<{ synced: boolean; error?: string }> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
    return { synced: false, error: 'Only iOS native' };
  }
  if (!options?.forceSync) {
    const connected = await getHealthConnectedState(userId);
    if (!connected) return { synced: false, error: 'Health not connected' };
  }

  try {
    const plugin = getStraivHealthConnect();
    if (!plugin) return { synced: false, error: 'Plugin not available' };

    // 1. Sync daily aggregates first (fast - for display and summary views)
    const result = await plugin.readLast30Days();
    const days = (result.days as Record<string, unknown>[]) ?? [];
    if (typeof window !== 'undefined') {
      console.log('[AppleHealth] readLast30Days returned', days.length, 'days');
    }
    if (days.length === 0) {
      // No days = Health not authorized or no data. Still notify the app so
      // cards/charts stop showing stale cached values after a foreground sync.
      if (typeof window !== 'undefined') {
        const now = Date.now();
        try {
          localStorage.setItem(LAST_SYNC_KEY(userId), now.toString());
        } catch {}
        window.dispatchEvent(new CustomEvent('health-sync-complete', {
          detail: { timestamp: now, empty: true },
        }));
      }
      return { synced: true };
    }

    const { supabase } = await import('@/integrations/supabase/client');

    // Fetch manual sleep logs - never overwrite these with HealthKit data
    const dateStrings = days.map((d) => d.metric_date as string).filter(Boolean);
    const { data: manualSleepRows } = await supabase
      .from('sleep_logs')
      .select('sleep_date, hours_slept')
      .eq('user_id', userId)
      .in('sleep_date', dateStrings);
    const manualSleepByDate = new Map<string, number>();
    for (const r of manualSleepRows ?? []) {
      const d = (r as { sleep_date: string; hours_slept: number }).sleep_date;
      const h = (r as { sleep_date: string; hours_slept: number }).hours_slept;
      if (d && h != null && h > 0) manualSleepByDate.set(d, h);
    }

    // Build today's local date string (used to decide which day is "today" for the user)
    const _now = new Date();
    const todayLocalStr = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;

    /**
     * Build all rows first, then batch upsert.
     *
     * Sync semantics (issue #1 fix):
     *  - For TODAY: include the row even if metrics are partial / zero, so steps/calories/etc.
     *    refresh independently of sleep.
     *  - SLEEP for today: if neither manual nor HealthKit returned sleep, write null (zero-out)
     *    — never keep yesterday's value. For older days we leave existing value (no overwrite to null)
     *    to avoid clobbering historical sleep when a partial sync runs.
     *  - For non-sleep columns we ONLY include values we have (Object pruning below). This
     *    means a partial HealthKit read can never wipe out previously-good data.
     */
    const upsertRows: Record<string, unknown>[] = [];
    for (const row of days) {
      const metricDate = row.metric_date as string;
      if (!metricDate) continue;
      const isToday = metricDate === todayLocalStr;

      const healthData = (row.health_data as Record<string, number>) ?? {};
      const healthDataClean = sanitizeHealthData(healthData);
      const hd = healthDataClean as Record<string, number>;

      // Oxygen saturation: native may return 0-1 (HealthKit percent) or 0-100; column expects 0-100
      const rawO2 = row.oxygenSaturation_pct as number | undefined;
      const oxygen_saturation_pct =
        rawO2 != null && Number.isFinite(rawO2)
          ? rawO2 <= 1
            ? sanitizeNumeric(rawO2 * 100, 100, 2)
            : sanitizeNumeric(rawO2, 100, 2)
          : null;

      const manualSleep = manualSleepByDate.get(metricDate);
      // Treat 0/negative/non-finite as "no data" (HealthKit can report a tiny
      // sleep interval that rounds to 0.00, and we don't want that erasing a
      // previously-good row in the DB).
      const rawAppleHealthSleep = sanitizeNumeric(row.sleep_hours as number, 24);
      const sleepHours = manualSleep != null && manualSleep > 0
        ? sanitizeNumeric(manualSleep, 24)
        : rawAppleHealthSleep != null && rawAppleHealthSleep > 0
        ? rawAppleHealthSleep
        : null;

      const allFields: Record<string, unknown> = {
        steps: sanitizeNumeric(row.steps as number, 2_147_483_647, 0),
        distance_m: sanitizeNumeric(row.distance_m as number, 99_999_999.99),
        active_energy_kcal: sanitizeNumeric(row.active_energy_kcal as number, 999_999.99),
        basal_energy_kcal: sanitizeNumeric(row.basal_energy_kcal as number, 999_999.99),
        flights_climbed: sanitizeNumeric(row.flights_climbed as number, 2_147_483_647, 0),
        exercise_minutes: sanitizeNumeric(row.exercise_minutes as number, 2_147_483_647, 0),
        stand_hours: sanitizeNumeric(row.stand_hours as number, 24, 0),
        heart_rate_min_bpm: sanitizeNumeric(row.heart_rate_min_bpm as number, 999.99),
        heart_rate_max_bpm: sanitizeNumeric(row.heart_rate_max_bpm as number, 999.99),
        heart_rate_avg_bpm: sanitizeNumeric(row.heart_rate_avg_bpm as number, 999.99),
        resting_heart_rate_bpm: sanitizeNumeric(row.resting_heart_rate_bpm as number, 999.99),
        weight_kg: sanitizeNumeric(row.weight_kg as number, 999.99),
        body_fat_pct: sanitizeNumeric(row.body_fat_pct as number, 99.99),
        height_cm: row.height_m != null ? sanitizeNumeric((row.height_m as number) * 100, 999.99) : null,
        vo2_max_ml_per_kg_min: sanitizeNumeric(row.vo2_max as number, 99.99),
        sleep_rem_hours: sanitizeNumeric(row.sleep_rem_hours as number, 24),
        sleep_deep_hours: sanitizeNumeric(row.sleep_deep_hours as number, 24),
        sleep_core_hours: sanitizeNumeric(row.sleep_core_hours as number, 24),
        sleep_awake_minutes: sanitizeNumeric(row.sleep_awake_minutes as number, 1440, 0),
        sleep_in_bed_hours: sanitizeNumeric(row.sleep_in_bed_hours as number, 24),
        hrv_sdnn_ms: sanitizeNumeric((row.heartRateVariabilitySDNN ?? hd?.heartRateVariabilitySDNN) as number, 9999.99),
        mindful_minutes: sanitizeNumeric(row.mindfulSession_minutes as number, 9999.99),
        distance_cycling_m: sanitizeNumeric((row.distanceCycling_m ?? hd?.distanceCycling) as number, 99_999_999.99),
        distance_swimming_m: sanitizeNumeric((row.distanceSwimming_m ?? hd?.distanceSwimming) as number, 99_999_999.99),
        blood_pressure_systolic: sanitizeNumeric(row.bloodPressureSystolic as number, 999, 0),
        blood_pressure_diastolic: sanitizeNumeric(row.bloodPressureDiastolic as number, 999, 0),
        respiratory_rate_per_min: sanitizeNumeric((row.respiratory_rate_per_min ?? hd?.respiratory_rate_per_min ?? hd?.HKQuantityTypeIdentifierRespiratoryRate) as number, 99.99),
        oxygen_saturation_pct,
        dietary_caffeine_mg: sanitizeNumeric((row.dietary_caffeine_mg ?? hd?.dietaryCaffeine ?? hd?.HKQuantityTypeIdentifierDietaryCaffeine) as number, 999_999.99),
        dietary_energy_kcal: sanitizeNumeric((row.dietaryEnergyConsumed_kcal ?? hd?.HKQuantityTypeIdentifierDietaryEnergyConsumed) as number, 999_999.99),
        dietary_protein_g: sanitizeNumeric((row.dietaryProtein_g ?? hd?.HKQuantityTypeIdentifierDietaryProtein) as number, 9999.99),
        dietary_carbs_g: sanitizeNumeric((row.dietaryCarbs_g ?? hd?.HKQuantityTypeIdentifierDietaryCarbohydrates) as number, 9999.99),
        dietary_fat_g: sanitizeNumeric((row.dietaryFat_g ?? hd?.HKQuantityTypeIdentifierDietaryFatTotal) as number, 9999.99),
        dietary_fiber_g: sanitizeNumeric((row.dietaryFiber_g ?? hd?.HKQuantityTypeIdentifierDietaryFiber) as number, 9999.99),
        dietary_sugar_g: sanitizeNumeric((row.dietarySugar_g ?? hd?.HKQuantityTypeIdentifierDietarySugar) as number, 9999.99),
        dietary_sodium_mg: sanitizeNumeric((row.dietarySodium_mg ?? hd?.HKQuantityTypeIdentifierDietarySodium) as number, 999_999.99),
        dietary_water_ml: sanitizeNumeric((row.dietaryWater_ml ?? hd?.HKQuantityTypeIdentifierDietaryWater) as number, 99_999_999.99),
        blood_alcohol_content: sanitizeNumeric(row.bloodAlcoholContent as number, 9.9999, 4),
        waist_cm: sanitizeNumeric(row.waist_cm as number, 999.99),
        basal_body_temp_c: sanitizeNumeric(row.basal_body_temp_c as number, 99.99),
        readiness_score: sanitizeNumeric(computeReadinessScore(row), 99.99),
        stress_score: sanitizeHealthScore0to100(computeStressScore(row)),
        strain: sanitizeHealthScore0to100(computeStrainScore(row)),
      };

      // Strip null fields so partial syncs never overwrite previously-good values.
      // For TODAY we keep `0` so a refreshed-but-empty Apple Health day shows real-time zeros.
      const cleanFields: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(allFields)) {
        if (v == null) continue;
        cleanFields[k] = v;
      }

      // Sleep is special: only write `sleep_hours` when we have a real,
      // positive value. We deliberately do NOT null today's column when fresh
      // data is missing - HealthKit attributes a sleep session to the day it
      // STARTED, so until the user starts a new bedtime that crosses midnight
      // today's row legitimately has no sleep yet (last night lives on
      // yesterday). Nulling here was the root cause of the "all zeros after
      // refresh" bug.
      if (sleepHours != null && sleepHours > 0) {
        cleanFields.sleep_hours = sleepHours;
      }

      upsertRows.push({
        user_id: userId,
        metric_date: metricDate,
        ...cleanFields,
        source: 'apple_health',
        ...(Object.keys(healthDataClean).length > 0 ? { health_data: healthDataClean } : {}),
      });
    }

    // Guarantee today is represented in the upsert set even if the plugin omitted it
    // (e.g. user didn't wear watch and HealthKit returned no aggregated row).
    // This is what makes other dashboards pick "today" instead of falling back to yesterday.
    if (!upsertRows.some((r) => r.metric_date === todayLocalStr)) {
      const manualSleepToday = manualSleepByDate.get(todayLocalStr);
      const todayRow: Record<string, unknown> = {
        user_id: userId,
        metric_date: todayLocalStr,
        source: 'apple_health',
      };
      // Only set sleep_hours if we genuinely have one; never write null, which
      // would clobber a previous row's sleep_hours via upsert.
      if (manualSleepToday != null && manualSleepToday > 0) {
        todayRow.sleep_hours = sanitizeNumeric(manualSleepToday, 24);
      }
      upsertRows.push(todayRow);
    }

    if (upsertRows.length > 0) {
      // Try full batch first; on overflow, retry in smaller batches (isolates bad row)
      const BATCH_SIZE = 15;
      let lastError: string | null = null;
      let successCount = 0;
      for (let i = 0; i < upsertRows.length; i += BATCH_SIZE) {
        const batch = upsertRows.slice(i, i + BATCH_SIZE);
        const { error } = await supabase.from('health_metrics').upsert(batch as any, { onConflict: 'user_id,metric_date' });
        if (error) {
          lastError = error.message;
          if (batch.length > 1) {
            // Retry row-by-row to salvage good rows
            for (const row of batch) {
              const { error: rowErr } = await supabase.from('health_metrics').upsert(row as any, { onConflict: 'user_id,metric_date' });
              if (!rowErr) successCount += 1;
            }
          }
        } else {
          successCount += batch.length;
        }
      }
      if (successCount === 0 && lastError) return { synced: false, error: lastError };
      if (typeof window !== 'undefined' && successCount > 0) {
        console.log('[AppleHealth] Upserted', successCount, 'days to health_metrics');
      }
    }

    // Sync menstrual and body measurements (rare per row)
    for (const row of days) {
      const metricDate = row.metric_date as string;
      if (!metricDate) continue;
      const hasMenstrual = row.menstrual_flow_value != null || row.cervical_mucus_quality != null || row.ovulation_test_result != null || row.basal_body_temp_c != null;
      if (hasMenstrual) {
        const menstrualRow = {
          user_id: userId,
          log_date: metricDate,
          menstrual_flow_value: row.menstrual_flow_value ?? null,
          cervical_mucus_quality: row.cervical_mucus_quality ?? null,
          ovulation_test_result: row.ovulation_test_result ?? null,
          basal_body_temp_c: row.basal_body_temp_c ?? null,
          source: 'apple_health',
        };
        await supabase.from('menstrual_logs').upsert(menstrualRow as any, { onConflict: 'user_id,log_date' });
      }
      if (row.waist_cm != null && (row.waist_cm as number) > 0) {
        const bodyRow = {
          user_id: userId,
          measurement_date: metricDate,
          waist_cm: row.waist_cm,
          source: 'apple_health',
        };
        await supabase.from('body_measurements_history').upsert(bodyRow as any, { onConflict: 'user_id,measurement_date' });
      }
    }

    // 2. Sync individual samples in background (can take 1–2 min; don't block)
    syncSamplesToSupabase(userId)
      .then((res) => {
        if (res.synced && res.count != null && res.count > 0) {
          console.log('[AppleHealth] Sample sync done:', res.count, 'samples');
        } else if (!res.synced && res.error) {
          console.error('[AppleHealth] Sample sync failed:', res.error);
        }
      })
      .catch((e) => {
        console.error('[AppleHealth] Sample sync error:', e instanceof Error ? e.message : e);
      });

    const hasData = hasAnyMetricData(days);
    // Only persist connected state when we got real data (user authorized and has metrics)
    if (hasData) {
      await persistHealthConnectedState(userId);
    }
    const now = Date.now();
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(LAST_SYNC_KEY(userId), now.toString());
      } catch {}
      window.dispatchEvent(new CustomEvent('health-sync-complete', { detail: { timestamp: now } }));
      // Show toast only when we have data, caller didn't skip, and last toast was > 5 min ago
      if (hasData && !options?.skipFeedback) {
        let canShow = true;
        try {
          const raw = localStorage.getItem(LAST_TOAST_KEY(userId));
          if (raw) {
            const last = parseInt(raw, 10);
            if (Number.isFinite(last) && now - last < TOAST_MIN_INTERVAL_MS) canShow = false;
          }
        } catch {}
        if (canShow) {
          try {
            localStorage.setItem(LAST_TOAST_KEY(userId), now.toString());
          } catch {}
          requestAnimationFrame(() => {
            setTimeout(() => {
              import('sonner').then(({ toast }) =>
                toast.success('Health data refreshed', {
                  duration: 5000,
                  id: 'health-sync-success',
                  style: { zIndex: 2147483647 },
                })
              );
            }, 200);
          });
          showFirstSyncSuccessFeedback();
        }
      }
    }
    return { synced: true };
  } catch (e) {
    if (typeof window !== 'undefined') {
      console.warn('[AppleHealth] syncHealthToSupabase error:', e);
    }
    return { synced: false, error: e instanceof Error ? e.message : 'Sync failed' };
  }
}

/** Get timestamp (ms) of last successful health sync. Per-user. */
export function getLastHealthSyncTimestamp(userId?: string): number | null {
  if (typeof window === 'undefined' || !userId) return null;
  try {
    const raw = localStorage.getItem(LAST_SYNC_KEY(userId));
    if (!raw) return null;
    const ts = parseInt(raw, 10);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

/** Opens the Apple Health app. Straiv appears in Health > Profile > Apps only after you grant permission via Connect. */
export const openHealthApp = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) return false;
  const HealthModule = await getHealth();
  if (!HealthModule) return false;
  const H = HealthModule as { openUrl?: (opts: { url: string }) => Promise<{ completed: boolean }> };
  if (typeof H.openUrl !== 'function') return false;
  try {
    const { completed } = await H.openUrl({ url: 'x-apple-health://' });
    return completed;
  } catch {
    try {
      window.location.href = 'x-apple-health://';
      return true;
    } catch {
      return false;
    }
  }
};
